import NewPostButton from './NewPostButton'
import styles from './_posts.module.scss'
import classNames from 'classnames'
import { t } from '../../../../utils'
import { useContext, useEffect, useRef, useState } from 'react'
import PostsFilter from './PostsFilter'
import PostItem from './PostItem'
import { useLazyQuery, useMutation } from '@apollo/client'
import {
  COMMUNITY_POSTS_QUERY,
  FOLLOW_CATEGORY_MUTATION,
  UNFOLLOW_CATEGORY_MUTATION
} from '../../../../gql'
import useScrollLoading from '../../../../utils/hooks/useScrollLoading'
import {
  CommunityCategory,
  CommunityPost,
  SortCommunityPostsEnum,
  User
} from '../../../../__generated__/graphql'
import { CommunityContext } from './CommunityLayout'
import useLabErrorHandling from '../../../../utils/hooks/useLabErrorHandling'
import FeaturedPosts from './FeaturedPosts'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPenToSquare } from '@fortawesome/free-solid-svg-icons'
import LoadingRing from '../../../../components/LoadingRing'

const PER_PAGE = 16

interface FiltersState {
  page: number
  perPage: number
  orderBy: SortCommunityPostsEnum
  categoryId: number
}

const initialFiltersState: FiltersState = {
  page: 1,
  perPage: PER_PAGE,
  orderBy: SortCommunityPostsEnum.Newest,
  categoryId: null
}

const ToggleFollowButton: React.FC<{
  topic: CommunityCategory
  communityMember: User
}> = ({ topic, communityMember }) => {
  const errorHandling = useLabErrorHandling()

  const [followCategory, { loading: followLoading }] = useMutation(
    FOLLOW_CATEGORY_MUTATION,
    {
      variables: { categoryId: topic.id },
      onError: errorHandling
    }
  )
  const [unfollowCategory, { loading: unfollowLoading }] = useMutation(
    UNFOLLOW_CATEGORY_MUTATION,
    {
      variables: { categoryId: topic.id }
    }
  )

  const isFollowing = !!communityMember.communityProfile.followCategories.find(
    (category) => category.id === topic.id
  )

  return (
    <button
      className={classNames(
        styles.followButton,
        isFollowing && styles.unfollowButton
      )}
      onClick={isFollowing ? () => unfollowCategory() : () => followCategory()}
      disabled={isFollowing ? unfollowLoading : followLoading}
    >
      {isFollowing ? t('Unfollow Topic') : t('Follow Topic')}
    </button>
  )
}

const Posts: React.FC<{ ssrPosts?: CommunityPost[] }> = ({ ssrPosts }) => {
  const { communityMember, topic } = useContext(CommunityContext)
  const initialPosts = ssrPosts || []
  const initialCategoryId = topic?.id ?? null

  const [filtersState, setFiltersState] = useState<FiltersState>({
    ...initialFiltersState,
    categoryId: initialCategoryId
  })
  const [searchResult, setSearchResult] =
    useState<CommunityPost[]>(initialPosts)
  const [featuredPosts, setFeaturedPosts] = useState<CommunityPost[]>(
    initialPosts.filter((post) => post.featured)
  )
  const [lastReceivedPageSize, setLastReceivedPageSize] = useState(
    initialPosts.length
  )

  const [selectedFilter, setSelectedFilter] = useState<SortCommunityPostsEnum>(
    SortCommunityPostsEnum.Newest
  )

  const [isPostsRequestPending, setIsPostsRequestPending] = useState(true)

  const [
    postsQuery,
    { data: postsData, loading: postsLoading, error: postsError }
  ] = useLazyQuery(COMMUNITY_POSTS_QUERY, {
    fetchPolicy: 'no-cache'
  })

  const postsListContainerRef = useRef(null)
  const doNewDataRequest = useRef(false)
  const lastRequestedPageRef = useRef(1)

  const doQuery = (filterState: FiltersState) => {
    lastRequestedPageRef.current = filterState.page
    postsQuery({ variables: filterState })
  }

  const loadNewDataInBottomPosition = () => {
    if (doNewDataRequest.current) return
    if (searchResult.length <= 0) return
    if (lastReceivedPageSize !== filtersState.perPage) return

    doNewDataRequest.current = true
    setFiltersState((prevState) => ({
      ...prevState,
      page: prevState.page + 1
    }))
  }

  useEffect(() => {
    doQuery(filtersState)
  }, [filtersState])

  useScrollLoading(postsListContainerRef, loadNewDataInBottomPosition, [
    filtersState.perPage,
    lastReceivedPageSize,
    searchResult.length
  ])

  useEffect(() => {
    if (postsData) {
      const newPosts = postsData.communityPosts as CommunityPost[]
      const requestedPage = lastRequestedPageRef.current
      setLastReceivedPageSize(newPosts.length)

      if (requestedPage === 1) {
        setSearchResult(newPosts)
        setFeaturedPosts(newPosts.filter((post) => post.featured))
      } else {
        setSearchResult((prevPosts) => {
          const allPosts = [...prevPosts, ...newPosts]
          const uniquePosts = Array.from(
            new Map(allPosts.map((post) => [post.id, post])).values()
          )
          const featuredPosts = uniquePosts.filter((post) => post.featured)
          setFeaturedPosts(featuredPosts)
          return uniquePosts
        })
      }
      doNewDataRequest.current = false
      setIsPostsRequestPending(false)
    }
  }, [postsData?.communityPosts])

  useEffect(() => {
    if (postsError) {
      doNewDataRequest.current = false
      setIsPostsRequestPending(false)
    }
  }, [postsError])

  const shouldSkipInitialResetRef = useRef(Boolean(ssrPosts?.length))

  useEffect(() => {
    if (shouldSkipInitialResetRef.current) {
      shouldSkipInitialResetRef.current = false
      return
    }

    resetSearch()
  }, [selectedFilter, topic?.id])

  const resetSearch = () => {
    doNewDataRequest.current = false
    setIsPostsRequestPending(true)
    setSearchResult([])
    setFiltersState((prevState) => ({
      ...prevState,
      page: 1,
      orderBy: selectedFilter,
      categoryId: topic?.id
    }))
  }

  const deleteFromSearchResult = (postId) => {
    setSearchResult((prevSearchResult) =>
      prevSearchResult.filter((post) => post.id !== postId)
    )
  }

  const isAllTopics = !topic?.title
  const isLoadingPosts = postsLoading || isPostsRequestPending
  const isInitialPageLoading = isLoadingPosts && filtersState.page === 1
  const isPaginationLoading = postsLoading && filtersState.page > 1

  const title = topic ? `#${topic.title}` : 'All Topics'
  const description = topic?.description

  return (
    <div className={styles.postsContainer}>
      <div
        className={classNames(
          styles.header,
          isAllTopics && styles.allTopicsHeader
        )}
      >
        <div className={styles.topicHeader}>
          <h1>{title}</h1>
          <div className={styles.topicHeaderContent}>
            <div className={styles.topicActionsContainer}>
              <div className={styles.topicActions}>
                {topic?.parentCategoryId && communityMember && (
                  <ToggleFollowButton
                    topic={topic}
                    communityMember={communityMember}
                  />
                )}
                <div className={styles.postsFilterContainer}>
                  <PostsFilter
                    selectedFilter={selectedFilter}
                    setSelectedFilter={setSelectedFilter}
                  />
                </div>
              </div>
            </div>
            <div>
              <NewPostButton />
            </div>
          </div>
        </div>

        {description && (
          <p className={styles.topicDescription}>{description}</p>
        )}
      </div>

      {isAllTopics && !isInitialPageLoading && featuredPosts.length > 0 && (
        <FeaturedPosts posts={featuredPosts} />
      )}

      <div ref={postsListContainerRef} className={styles.postsList}>
        {isInitialPageLoading ? (
          <LoadingRing size={48} color="#7479dc" />
        ) : searchResult?.length ? (
          <>
            {searchResult.map((post) => (
              <PostItem
                key={post.id}
                post={post}
                onPostRemove={() => deleteFromSearchResult(post.id)}
                onPostPinToggle={resetSearch}
              />
            ))}
            {isPaginationLoading && <LoadingRing size={48} color="#7479dc" />}
          </>
        ) : (
          !postsError && (
            <div className={styles.noPostsContainer}>
              <FontAwesomeIcon icon={faPenToSquare} size="2x" color="#888" />
              <p>{t('There are no posts in this topic yet. Be the first!')}</p>
            </div>
          )
        )}
      </div>
    </div>
  )
}

export default Posts
